import type { Shape }        from '@tokamap/core';
import type { ShapeBadge }   from '@tokamap/core';
import themeStore            from '@/TokamapEditor/stores/themeStore';
import type { Theme }        from '@/TokamapEditor/stores/themeStore';
import { MULTIPLE_THEMES }   from '@/TokamapEditor/helpers/themeImposed';
import type { ThemeLockRef } from '@/TokamapEditor/helpers/themeImposed';

const BADGE_KINDS: Shape['shapeKind'][] = [undefined, 'polygon', 'rectangle', 'circle', 'marker'];

/** Les formes dont l'image et le texte peuvent s'afficher en marqueur (voir `Shape.badge`). */
export function badgeEligibleShapes(shapes: Shape[]): Shape[] {
	return shapes.filter(shape => BADGE_KINDS.includes(shape.shapeKind));
}

/** Le thème qui impose à la forme d'afficher son image et son texte dedans ou en marqueur, s'il y en a un. */
export function badgeImposingTheme(shape: Shape): Theme | undefined {
	const theme = shape.themeId ? themeStore.getTheme(shape.themeId) : undefined;

	return theme?.badge !== undefined ? theme : undefined;
}

/** Le marqueur affiché : ce qu'impose le thème, complété par celui de la forme — même règle que le cœur. */
export function effectiveBadge(shape: Shape): ShapeBadge | undefined {
	const imposed = badgeImposingTheme(shape)?.badge;
	const imposedFields = imposed ? Object.fromEntries(Object.entries(imposed).filter(([, value]) => value !== undefined)) : {};

	if (shape.shapeKind === 'marker') return { style: 'pin', ...shape.badge, ...imposedFields };
	if (imposed === undefined) return shape.badge;
	if (imposed === false) return undefined;

	return { ...shape.badge, ...imposedFields };
}

/** Le thème qui impose ce réglage du marqueur à au moins une forme, ou l'affichage même du marqueur sans `field`. */
export function badgeFieldLock(shapes: Shape[], field?: keyof ShapeBadge): ThemeLockRef | undefined {
	const themes = [...new Map(shapes.flatMap(shape => {
		const theme = badgeImposingTheme(shape);

		if (!theme) return [];

		return !field || (theme.badge && theme.badge[field] !== undefined) ? [[theme.id, theme] as const] : [];
	})).values()];

	if (!themes.length) return undefined;
	if (themes.length > 1) return { theme: MULTIPLE_THEMES };

	return {
		theme: themes[0]!.name,
		target: { themeId: themes[0]!.id, section: 'content', field: field ? `badge.${field}` : 'badge' },
	};
}

/** Vrai quand toutes les formes de la sélection affichent leur image et leur texte en marqueur, thème compris. */
export function showsBadge(shapes: Shape[]): boolean {
	const eligible = badgeEligibleShapes(shapes);

	return !!eligible.length && eligible.every(shape => !!effectiveBadge(shape));
}

/** Sans réglage, le texte passe sous la pastille et au-dessus de l'épingle. */
export function textPositionOf(badge: ShapeBadge | undefined) {
	return badge?.textPosition ?? (badge?.style === 'pin' ? 'above' : 'below');
}
